import React, { useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import type * as sched from "sched";

export type FuzzyScheduleResult = sched.FuzzyScheduleResult;

// ─── Pool colors ─────────────────────────────────────────────────────

const POOL_COLORS = [
  "#e07850", "#5088c0", "#60a060", "#c070c0",
  "#d0a040", "#50b0b0", "#b06060", "#7080d0",
  "#a0c050", "#d070a0", "#6090a0", "#c09060",
];

// ─── Layout constants ────────────────────────────────────────────────

const ROW_H = 42;
const ROW_GAP = 3;
const LABEL_W = 300;
const TOP_M = 52;
const BOT_M = 20;
const EPSILON = 1e-10;
const MAX_LABEL_CHARS = 40;
const MIN_CHART_DAYS = 30; // minimum days to show so chart fills container

// ─── Component ───────────────────────────────────────────────────────

type GanttRow =
  | { type: "pool"; poolLabel: string; color: string; workerCount: number }
  | {
      type: "task";
      nodeId: string;
      label: string;
      startDist: Distribution;
      finishDist: Distribution;
      startStats: DistributionStats;
      finishStats: DistributionStats;
      rate: number;
      poolLabel: string;
      color: string;
      conditional: boolean;
    };

export const FuzzyGanttChart: React.FC<{
  result: FuzzyScheduleResult;
  onStepClick?: (stepId: string) => void;
  /** When set, X-axis shows calendar dates instead of "Day N". */
  startDate?: Date;
  /** Minimum number of days to show (chart extends at least this far). */
  minDays?: number;
  /** Maps node ID → entity type/id for linking to detail pages. */
  nodeToEntity?: Record<string, { type: string; id: string }>;
  /** URL prefix for entity detail links (e.g. "/workspaces/abc"). */
  linkBase?: string;
}> = ({ result, onStepClick, startDate, minDays, nodeToEntity, linkBase }) => {
  const navigate = useNavigate();
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredRowIdx, setHoveredRowIdx] = useState<number | null>(null);
  const [showMarkers, setShowMarkers] = useState(true);
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Measure container width on mount and resize
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    setContainerWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  /** Build a link path for an entity, or null if not available. */
  const entityLink = useCallback((nodeId: string): string | null => {
    if (!nodeToEntity || !linkBase) return null;
    const entity = nodeToEntity[nodeId];
    if (!entity) return null;
    const slug = entity.type.toLowerCase() + 's'; // "Task" → "tasks", "Outcome" → "outcomes"
    return `${linkBase}/${slug}/${entity.id}`;
  }, [nodeToEntity, linkBase]);

  /** Format a day offset as either a calendar date or "day N" depending on startDate. */
  const fmtDay = useCallback((day: number) => {
    if (startDate) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + Math.round(day));
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return `day ${day.toFixed(1)}`;
  }, [startDate]);

  const exportPng = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);
    const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    const scale = 2; // 2x for retina
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = svg.width.baseVal.value * scale;
      canvas.height = svg.height.baseVal.value * scale;
      const ctx = canvas.getContext("2d")!;
      ctx.scale(scale, scale);
      // Dark background
      ctx.fillStyle = "#030712";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "schedule.png";
        a.click();
        URL.revokeObjectURL(a.href);
      }, "image/png");
    };
    img.src = url;
  }, []);

  const { schedule, meta, stats } = result;

  // Assign a color to each pool
  const poolColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    const pools = Object.keys(meta.pool_resources).sort();
    pools.forEach((label, i) => {
      map[label] = POOL_COLORS[i % POOL_COLORS.length];
    });
    return map;
  }, [meta.pool_resources]);

  const conditionalSet = useMemo(
    () => new Set(meta.conditional_nodes.map(String)),
    [meta.conditional_nodes],
  );

  // Find which pool each node belongs to (via resource eligibility)
  const nodePoolMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const [nodeId, item] of Object.entries(schedule.items)) {
      const contribs = item.resource_contributions;
      const resourceIds = Object.keys(contribs);
      if (resourceIds.length > 0) {
        // Pick the pool of the first contributing resource
        const rid = resourceIds[0];
        map[nodeId] = meta.resource_to_pool[rid] || "unknown";
      }
    }
    return map;
  }, [schedule.items, meta.resource_to_pool]);

  const { rows, maxDay, tickInterval } = useMemo(() => {
    // Group nodes by pool
    const grouped: Record<string, string[]> = {};
    for (const pool of Object.keys(meta.pool_resources)) {
      grouped[pool] = [];
    }
    // Add an "ungrouped" bucket for nodes without a pool (outcomes, delays, etc.)
    grouped["_ungrouped"] = [];

    for (const nodeId of Object.keys(schedule.items)) {
      const pool = nodePoolMap[nodeId];
      if (pool && grouped[pool]) {
        grouped[pool].push(nodeId);
      } else {
        grouped["_ungrouped"].push(nodeId);
      }
    }

    const rows: GanttRow[] = [];
    let maxDay = 0;

    // Sort pools, keeping _ungrouped (outcomes, milestones) last
    const sortedPools = Object.keys(grouped).sort((a, b) => {
      if (a === "_ungrouped") return 1;
      if (b === "_ungrouped") return -1;
      return a.localeCompare(b);
    });

    for (const pool of sortedPools) {
      const nodeIds = grouped[pool];
      if (nodeIds.length === 0) continue;

      // Sort by P50 start time
      nodeIds.sort((a, b) => (stats[a]?.start.p50 ?? 0) - (stats[b]?.start.p50 ?? 0));

      const poolDisplay = pool === "_ungrouped" ? "Outcomes" : formatPoolLabel(pool);
      const color = poolColorMap[pool] || "#888";
      const workerCount = meta.pool_resources[pool]?.length ?? 0;

      rows.push({ type: "pool", poolLabel: poolDisplay, color, workerCount });

      for (const nodeId of nodeIds) {
        const item = schedule.items[nodeId];
        const nodeStat = stats[nodeId];
        if (!item || !nodeStat) continue;

        maxDay = Math.max(maxDay, nodeStat.finish.p90);

        rows.push({
          type: "task",
          nodeId,
          label: meta.node_labels[nodeId] || `Node ${nodeId}`,
          startDist: item.start,
          finishDist: item.finish,
          startStats: nodeStat.start,
          finishStats: nodeStat.finish,
          rate: item.effective_rate,
          poolLabel: poolDisplay,
          color,
          conditional: conditionalSet.has(nodeId),
        });
      }
    }

    // Enforce minimum date range
    const effectiveMinDays = minDays ?? MIN_CHART_DAYS;
    maxDay = Math.max(maxDay, effectiveMinDays);

    // Pick a nice tick interval targeting ~6-8 labels
    const TARGET_TICKS = 7;
    const rawInterval = Math.max(maxDay, 1) / TARGET_TICKS;
    const niceSteps = [1, 2, 5, 7, 14, 21, 28, 30, 60, 90, 180, 365];
    const tickInterval = niceSteps.find(s => s >= rawInterval) ?? Math.ceil(rawInterval / 100) * 100;
    const ceilDay = Math.ceil(maxDay / tickInterval) * tickInterval + tickInterval;
    maxDay = ceilDay;

    // dayScale will be computed in a second pass using container width
    const dayScale = 0; // placeholder — computed below
    return { rows, maxDay, dayScale, tickInterval };
  }, [schedule, meta, stats, nodePoolMap, poolColorMap, conditionalSet, minDays]);

  // Compute dayScale from container width so the chart fills available space
  const dayScale = useMemo(() => {
    if (containerWidth <= 0) return maxDay > 0 ? 720 / maxDay : 10;
    const availableWidth = containerWidth - LABEL_W - 60; // padding + margins
    return Math.max(availableWidth / maxDay, 2);
  }, [containerWidth, maxDay]);

  const chartW = maxDay * dayScale;
  const totalH = TOP_M + rows.length * (ROW_H + ROW_GAP) + BOT_M;

  const renderBand = useCallback(
    (row: Extract<GanttRow, { type: "task" }>, y: number) => {
      const { startDist: sd, finishDist: fd, color, conditional, startStats: ss, finishStats: fs } = row;

      // Clip rendering to P10-P90 range to avoid thin tails
      const clipStart = ss.p10;
      const clipEnd = fs.p90;

      const bandH = ROW_H - 8;
      const bw = Math.max(dayScale * sd.resolution, 1);
      const rects: React.ReactElement[] = [];

      const minT = sd.offset;
      const maxT = fd.offset + fd.pdf.length;
      const span = maxT - minT;
      if (span <= 0) return null;

      // Build active curve
      const active = new Float64Array(span);
      let startCum = 0, finishCum = 0, maxActive = 0;
      for (let t = 0; t < span; t++) {
        const si = t + minT - sd.offset;
        if (si >= 0 && si < sd.pdf.length) startCum += sd.pdf[si];
        const fi = t + minT - fd.offset;
        if (fi >= 0 && fi < fd.pdf.length) finishCum += fd.pdf[fi];
        active[t] = Math.max(0, startCum - finishCum);
        maxActive = Math.max(maxActive, active[t]);
      }

      if (maxActive < EPSILON) {
        // Zero-effort item (outcome, milestone, etc.): render finish PDF
        // as "probability of completion on this date"
        let maxPdf = 0;
        for (let i = 0; i < fd.pdf.length; i++) {
          maxPdf = Math.max(maxPdf, fd.pdf[i]);
        }
        if (maxPdf < EPSILON) return null;

        for (let i = 0; i < fd.pdf.length; i++) {
          if (fd.pdf[i] < EPSILON) continue;
          const dayVal = (fd.offset + i) * fd.resolution;
          if (dayVal < clipStart || dayVal > clipEnd) continue;
          const x = dayVal * dayScale;
          const opacity = (fd.pdf[i] / maxPdf) * 0.75 + 0.05;
          rects.push(
            <rect
              key={i}
              x={LABEL_W + x}
              y={y + 4}
              width={bw + 0.5}
              height={bandH}
              fill={color}
              opacity={opacity}
            />,
          );
        }
        return rects;
      }

      for (let t = 0; t < span; t++) {
        if (active[t] < EPSILON) continue;
        const dayVal = (minT + t) * sd.resolution;
        if (dayVal < clipStart || dayVal > clipEnd) continue;
        const x = dayVal * dayScale;
        const opacity = (active[t] / maxActive) * 0.75 + 0.05;

        rects.push(
          <rect
            key={t}
            x={LABEL_W + x}
            y={y + 4}
            width={bw + 0.5}
            height={bandH}
            fill={color}
            opacity={opacity}
          />,
        );

        // Conditional hatching: diagonal lines at reduced opacity
        if (conditional && active[t] / maxActive > 0.3) {
          rects.push(
            <rect
              key={`h-${t}`}
              x={LABEL_W + x}
              y={y + 4}
              width={bw + 0.5}
              height={bandH}
              fill="url(#conditional-hatch)"
              opacity={opacity * 0.4}
            />,
          );
        }
      }
      return rects;
    },
    [dayScale],
  );

  const hovRow = hoveredNode
    ? (rows.find((r) => r.type === "task" && r.nodeId === hoveredNode) as
        | Extract<GanttRow, { type: "task" }>
        | undefined)
    : null;

  return (
    <div
      ref={containerRef}
      className="overflow-x-auto rounded-xl border border-gray-800 bg-gray-950 p-5"
      style={{ fontFamily: "'IBM Plex Mono', 'SF Mono', 'Menlo', monospace" }}
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-[11px] text-gray-500">
            Grouped by work pool · brighter = higher likelihood
            {conditionalSet.size > 0 && " · hatched = conditional"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showMarkers}
              onChange={(e) => setShowMarkers(e.target.checked)}
              className="accent-gray-500"
            />
            P10 / P50 / P90
          </label>
          <button
            onClick={exportPng}
            className="text-[11px] text-gray-500 hover:text-gray-300 transition-colors px-2 py-0.5 rounded border border-gray-700 hover:border-gray-500"
          >
            Export PNG
          </button>
        </div>
      </div>

      <div className="relative">
      <svg
        ref={svgRef}
        xmlns="http://www.w3.org/2000/svg"
        width={LABEL_W + chartW + 40}
        height={totalH}
        className="block"
        style={{ fontFamily: "inherit" }}
      >
        {/* Conditional hatch pattern */}
        <defs>
          <pattern
            id="conditional-hatch"
            width={6}
            height={6}
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line x1={0} y1={0} x2={0} y2={6} stroke="#fff" strokeWidth={1} />
          </pattern>
        </defs>

        {/* Day grid lines */}
        {Array.from({ length: Math.floor(maxDay / tickInterval) + 1 }, (_, i) => {
          const day = i * tickInterval;
          const x = LABEL_W + day * dayScale;
          return (
            <g key={day}>
              <line
                x1={x}
                y1={TOP_M - 10}
                x2={x}
                y2={totalH - BOT_M}
                stroke="#1e2330"
              />
              <text
                x={x}
                y={TOP_M - 18}
                textAnchor="middle"
                fill="#4a5568"
                fontSize={10}
                fontFamily="inherit"
              >
                {startDate ? formatTickDate(startDate, day) : `Day ${day}`}
              </text>
            </g>
          );
        })}

        {/* Rows */}
        {rows.map((row, idx) => {
          const y = TOP_M + idx * (ROW_H + ROW_GAP);

          if (row.type === "pool") {
            return (
              <g key={`pool-${row.poolLabel}`}>
                <rect
                  x={0}
                  y={y}
                  width={LABEL_W + chartW + 40}
                  height={ROW_H}
                  fill="#141822"
                  rx={4}
                />
                <circle cx={14} cy={y + ROW_H / 2} r={5} fill={row.color} />
                <text
                  x={26}
                  y={y + ROW_H / 2 + 4}
                  fill={row.color}
                  fontSize={12}
                  fontWeight={600}
                  fontFamily="inherit"
                >
                  {row.poolLabel}
                </text>
                {row.workerCount > 0 && (
                  <text
                    x={LABEL_W - 8}
                    y={y + ROW_H / 2 + 3}
                    fill="#3a4258"
                    fontSize={9}
                    textAnchor="end"
                    fontFamily="inherit"
                  >
                    {row.workerCount} worker{row.workerCount !== 1 ? "s" : ""}
                  </text>
                )}
              </g>
            );
          }

          const isHov = hoveredNode === row.nodeId;

          return (
            <g
              key={row.nodeId}
              onMouseEnter={() => { setHoveredNode(row.nodeId); setHoveredRowIdx(idx); }}
              onMouseLeave={() => { setHoveredNode(null); setHoveredRowIdx(null); }}
              onClick={() => {
                const link = entityLink(row.nodeId);
                if (link) {
                  navigate(link);
                } else {
                  const stepId = meta.node_to_step?.[row.nodeId];
                  if (stepId && onStepClick) onStepClick(stepId);
                }
              }}
              style={{ cursor: "pointer" }}
            >
              {isHov && (
                <rect
                  x={0}
                  y={y - 1}
                  width={LABEL_W + chartW + 40}
                  height={ROW_H + 2}
                  fill="#1a2030"
                  rx={4}
                />
              )}
              <text
                x={20}
                y={y + ROW_H / 2 + 4}
                fill={isHov ? "#e8ecf4" : "#8892a8"}
                fontSize={11}
                fontFamily="inherit"
              >
                {row.label.length > MAX_LABEL_CHARS
                  ? row.label.slice(0, MAX_LABEL_CHARS - 1) + "…"
                  : row.label}
                <title>{row.label}</title>
              </text>
              {row.conditional && (
                <text
                  x={LABEL_W - 8}
                  y={y + ROW_H / 2 + 3}
                  fill="#b08030"
                  fontSize={9}
                  textAnchor="end"
                  fontFamily="inherit"
                >
                  conditional
                </text>
              )}

              {renderBand(row, y)}

              {showMarkers && (
                <>
                  <line
                    x1={LABEL_W + row.finishStats.p50 * dayScale}
                    y1={y + 6}
                    x2={LABEL_W + row.finishStats.p50 * dayScale}
                    y2={y + ROW_H - 6}
                    stroke="#fff"
                    strokeWidth={1.5}
                    opacity={0.6}
                  />
                  <line
                    x1={LABEL_W + row.finishStats.p10 * dayScale}
                    y1={y + ROW_H / 2 - 6}
                    x2={LABEL_W + row.finishStats.p10 * dayScale}
                    y2={y + ROW_H / 2 + 6}
                    stroke="#fff"
                    strokeWidth={1}
                    opacity={0.3}
                  />
                  <line
                    x1={LABEL_W + row.finishStats.p90 * dayScale}
                    y1={y + ROW_H / 2 - 6}
                    x2={LABEL_W + row.finishStats.p90 * dayScale}
                    y2={y + ROW_H / 2 + 6}
                    stroke="#fff"
                    strokeWidth={1}
                    opacity={0.3}
                  />
                </>
              )}
            </g>
          );
        })}
      </svg>

      {/* Floating tooltip — positioned near the hovered row */}
      {hovRow && hoveredRowIdx !== null && (
        <div
          className="absolute pointer-events-none rounded-lg bg-gray-900/95 border border-gray-700 p-2.5 text-[11px] shadow-xl z-10"
          style={{
            top: TOP_M + hoveredRowIdx * (ROW_H + ROW_GAP) - 10,
            left: LABEL_W + 20,
            maxWidth: 340,
          }}
        >
          <div className="text-gray-200 font-semibold mb-1.5">
            {hovRow.label}
            {hovRow.conditional && (
              <span className="ml-1.5 text-amber-500 font-normal text-[10px]">CONDITIONAL</span>
            )}
          </div>
          {(() => {
            // Use pre-computed dates from Rust when available, fall back to JS conversion
            const fd = (s: any) => s.p10_date ? fmtDateStr(s.p10_date) : fmtDay(s.p10);
            const fm = (s: any) => s.p50_date ? fmtDateStr(s.p50_date) : fmtDay(s.p50);
            const fh = (s: any) => s.p90_date ? fmtDateStr(s.p90_date) : fmtDay(s.p90);
            return hovRow.rate < EPSILON ? (
              <div className="grid grid-cols-1 gap-y-1 text-[10px]">
                <div><span className="text-gray-500">Completion:</span> {fd(hovRow.finishStats)} / {fm(hovRow.finishStats)} / {fh(hovRow.finishStats)}</div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                <div><span className="text-gray-500">Start:</span> {fd(hovRow.startStats)} / {fm(hovRow.startStats)} / {fh(hovRow.startStats)}</div>
                <div><span className="text-gray-500">Finish:</span> {fd(hovRow.finishStats)} / {fm(hovRow.finishStats)} / {fh(hovRow.finishStats)}</div>
                <div><span className="text-gray-500">Duration:</span> {(hovRow.finishStats.p10 - hovRow.startStats.p90).toFixed(1)} / {(hovRow.finishStats.p50 - hovRow.startStats.p50).toFixed(1)} / {(hovRow.finishStats.p90 - hovRow.startStats.p10).toFixed(1)}d</div>
              </div>
            );
          })()}
        </div>
      )}
      </div>
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────

/** Format a tick day offset as a calendar date. */
function formatTickDate(startDate: Date, dayOffset: number): string {
  const d = new Date(startDate);
  d.setDate(d.getDate() + dayOffset);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Format an ISO date string (YYYY-MM-DD) as "Mon DD". */
function fmtDateStr(iso: string): string {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Format a raw pool label like "param:developer" → "developer" */
function formatPoolLabel(label: string): string {
  if (label.startsWith("param:")) return label.slice(6);
  if (label.startsWith("team:")) return `Team ${label.slice(5)}`;
  if (label.startsWith("users:")) return `Users ${label.slice(6)}`;
  if (label === "everyone") return "Everyone";
  return label;
}

export default FuzzyGanttChart;
